import React, { useState, useEffect } from 'react';
import { patientsService, Patient, CreatePatientData } from '@services/patientsService';
import AnimalCard from './AnimalCard';
import { Box, Card, CardContent, Typography, Button, Chip, IconButton, Tooltip, Paper, List, ListItemText, ListItemIcon, Divider, Avatar, Stack, TextField, FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Badge, Alert, LinearProgress, Fab, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Snackbar } from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Pets as PetsIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  QrCode as QrIcon,
  LocalHospital as HospitalIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  MoreVert as MoreIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';
import StickyFilters from '@shared/StickyFilters';
import ActionButtons from '@shared/ActionButtons';


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`patients-tabpanel-${index}`}
      aria-labelledby={`patients-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const PatientsManagement: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecies, setSelectedSpecies] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newPatient, setNewPatient] = useState({
    name: '',
    species: '',
    breed: '',
    gender: '',
    birthDate: '',
    weight: '',
    ownerName: '',
    ownerPhone: '',
    ownerEmail: '',
    ownerAddress: '',
    chipId: '',
    lastVaccination: '',
    allergies: '',
    medicalNotes: ''
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [selectedAnimalId, setSelectedAnimalId] = useState<string | null>(null);

  // Incarcare pacienti - va fi inlocuit cu apeluri API reale
  useEffect(() => {
    const loadPatients = async () => {
      setLoading(true);
      
      try {
        if (!user?.clinicId) {
          console.warn('Clinic ID is not available. Cannot load patients.');
          setPatients([]);
          return;
        }
        
        const patientsResponse = await patientsService.getPatients({
          clinicId: user.clinicId,
          status: selectedStatus === 'all' ? undefined : selectedStatus as 'active' | 'deceased' | 'transferred',
          search: searchTerm
        });
        setPatients(patientsResponse.patients);
        
      } catch (error) {
        console.error('Eroare la incarcarea pacientilor:', error);
        // In caz de eroare, afisam array gol
        setPatients([]);
      } finally {
        setLoading(false);
      }
    };

    loadPatients();
  }, [selectedStatus, searchTerm, user?.clinicId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'deceased':
        return 'error';
      case 'transferred':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Activ';
      case 'deceased':
        return 'Decedat';
      case 'transferred':
        return 'Transferat';
      default:
        return status;
    }
  };

  const getSexLabel = (sex: string) => {
    return sex === 'male' ? 'Mascul' : 'Feminin';
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCreatePatient = () => {
    setCreateDialogOpen(true);
  };

  const handleGenerateQRCode = (patientId: string) => {
    // Implementare generare QR Code pentru pacient
    console.log('Genereaza QR Code pentru pacient:', patientId);
    // Aici se poate deschide un dialog cu QR Code-ul sau naviga la o pagina cu QR Code
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setNewPatient({
      name: '',
      species: '',
      breed: '',
      gender: '',
      birthDate: '',
      weight: '',
      ownerName: '',
      ownerPhone: '',
      ownerEmail: '',
      ownerAddress: '',
      chipId: '',
      lastVaccination: '',
      allergies: '',
      medicalNotes: ''
    });
  };

  const handleSavePatient = async () => {
    try {
      // Validare campuri obligatorii
      if (!newPatient.name || !newPatient.species || !newPatient.gender || !newPatient.chipId || !newPatient.ownerName || !newPatient.ownerPhone) {
        setSnackbarMessage('Toate campurile marcate cu * sunt obligatorii, inclusiv Cip ID pentru identificarea unica a animalului.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      // Pentru crearea automata a clientului, folosim un ID temporar
      const tempOwnerId = 'temp-owner-id';
      
      const patientData: CreatePatientData = {
        name: newPatient.name,
        species: newPatient.species,
        breed: newPatient.breed,
        sex: (newPatient.gender === 'mascul' ? 'male' : 'female') as 'male' | 'female',
        age: newPatient.birthDate ? Math.floor((Date.now() - new Date(newPatient.birthDate).getTime()) / (365.25 * 24 * 60 * 60 * 1000)) : 0,
        weight: parseFloat(newPatient.weight) || undefined,
        color: 'Necunoscuta',
        microchip: newPatient.chipId || undefined,
        dateOfBirth: newPatient.birthDate || undefined,
        ownerId: tempOwnerId, // Backend va crea automat clientul
        tags: [],
        allergies: newPatient.allergies ? [newPatient.allergies] : [],
        temperament: undefined,
        sensitivities: [],
        // Informatii despre proprietar pentru crearea automata
        ownerName: newPatient.ownerName,
        ownerPhone: newPatient.ownerPhone,
        ownerEmail: newPatient.ownerEmail
      };

      console.log('Salvare pacient:', patientData);
      
      // Salvare in backend
      const createdPatient = await patientsService.createPatient(patientData);
      
      if (createdPatient) {
        console.log('Pacient creat cu succes:', createdPatient);
        
        // Adauga pacientul in lista locala
        setPatients(prev => [createdPatient, ...prev]);
        
        // Afiseaza mesaj de succes
        setSnackbarMessage('Pacientul a fost creat cu succes!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        console.error('Eroare la crearea pacientului in backend');
        setSnackbarMessage('Eroare la crearea pacientului.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      setCreateDialogOpen(false);
      setNewPatient({
        name: '',
        species: '',
        breed: '',
        gender: '',
        birthDate: '',
        weight: '',
        ownerName: '',
        ownerPhone: '',
        ownerEmail: '',
        ownerAddress: '',
        chipId: '',
        lastVaccination: '',
        allergies: '',
        medicalNotes: ''
      });
      
      
    } catch (error) {
      console.error('Eroare la crearea pacientului:', error);
      setSnackbarMessage('Eroare la crearea pacientului.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewAnimal = (animalId: string) => {
    setSelectedAnimalId(animalId);
  };

  const handleCloseAnimalCard = () => {
    setSelectedAnimalId(null);
  };

  const filteredPatients = patients.filter(patient => {
    const matchesSearch = patient.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         patient.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         patient.breed?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSpecies = selectedSpecies === 'all' || patient.species === selectedSpecies;
    const matchesStatus = selectedStatus === 'all' || patient.status === selectedStatus;
    
    return matchesSearch && matchesSpecies && matchesStatus;
  });

  const activePatients = filteredPatients.filter(p => p.status === 'active');
  const inactivePatients = filteredPatients.filter(p => p.status === 'transferred');
  const allPatients = filteredPatients;

  const isVaccinationOverdue = (nextVaccination: string) => {
    if (!nextVaccination) return false;
    const today = new Date();
    const vaccinationDate = new Date(nextVaccination);
    return vaccinationDate < today;
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Se incarca pacientii...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Pacienti
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestioneaza animalele si istoricul lor medical - CRUD, alergii, flaguri, atasamente
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreatePatient}
          sx={{ borderRadius: 2 }}
        >
          Pacient Nou
        </Button>
      </Box>

      {/* Filtre Sticky */}
      <StickyFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedType={selectedSpecies}
        onTypeChange={setSelectedSpecies}
        typeOptions={[
          { value: 'dog', label: 'Câine' },
          { value: 'cat', label: 'Pisică' },
          { value: 'bird', label: 'Pasăre' },
          { value: 'rabbit', label: 'Iepure' },
          { value: 'horse', label: 'Cal' },
          { value: 'other', label: 'Altele' }
        ]}
        selectedStatus={selectedStatus}
        onStatusChange={setSelectedStatus}
        statusOptions={[
          { value: 'active', label: 'Activ' },
          { value: 'inactive', label: 'Inactiv' },
          { value: 'deceased', label: 'Decedat' }
        ]}
        onRefresh={() => window.location.reload()}
        onAdvancedFilters={() => {}}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 3 }}>
            <Button
              variant="outlined"
              startIcon={<WarningIcon />}
              fullWidth
              sx={{ height: 56, borderRadius: 2 }}
            >
              Vaccin in Intarziere
            </Button>
          </Grid>
        </Grid>
      </StickyFilters>

      {/* Tabs */}
      <Card sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="patients tabs">
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Activi
                  <Badge badgeContent={activePatients.length} color="success" />
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Inactivi
                  <Badge badgeContent={inactivePatients.length} color="warning" />
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Toti
                  <Badge badgeContent={allPatients.length} color="primary" />
                </Box>
              } 
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Pacient</TableCell>
                  <TableCell>Specie/Rasa</TableCell>
                  <TableCell>Proprietar</TableCell>
                  <TableCell sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}>
                    Ultima Vizita
                  </TableCell>
                  <TableCell sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}>
                    Vaccinare
                  </TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activePatients
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((patient) => (
                    <TableRow key={patient.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.light' }}>
                            <PetsIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {patient.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {getSexLabel(patient.sex)} • {patient.weight || 'N/A'}kg
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {patient.species}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {patient.breed}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {patient.clientName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          N/A
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          N/A
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">
                            {patient.nextVaccinationDue || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(patient.status)}
                          color={getStatusColor(patient.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <ActionButtons
                          onView={() => handleViewAnimal(patient.id)}
                          onEdit={() => {}}
                          customActions={[
                            {
                              icon: <QrIcon />,
                              tooltip: 'QR Code',
                              onClick: () => handleGenerateQRCode(patient.id)
                            }
                          ]}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={activePatients.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Pacient</TableCell>
                  <TableCell>Specie/Rasa</TableCell>
                  <TableCell>Proprietar</TableCell>
                  <TableCell>Ultima Vizita</TableCell>
                  <TableCell>Vaccinare</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inactivePatients
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((patient) => (
                    <TableRow key={patient.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'warning.light' }}>
                            <PetsIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {patient.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {getSexLabel(patient.sex)} • {patient.weight || 'N/A'}kg
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {patient.species}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {patient.breed}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {patient.clientName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          N/A
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          N/A
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          N/A
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(patient.status)}
                          color={getStatusColor(patient.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <ActionButtons
                          onView={() => handleViewAnimal(patient.id)}
                          onEdit={() => {}}
                          customActions={[
                            {
                              icon: <QrIcon />,
                              tooltip: 'QR Code',
                              onClick: () => handleGenerateQRCode(patient.id)
                            }
                          ]}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={inactivePatients.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Pacient</TableCell>
                  <TableCell>Specie/Rasa</TableCell>
                  <TableCell>Proprietar</TableCell>
                  <TableCell>Ultima Vizita</TableCell>
                  <TableCell>Vaccinare</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allPatients
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((patient) => (
                    <TableRow key={patient.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ 
                            bgcolor: patient.status === 'active' ? 'primary.light' : 
                                    patient.status === 'transferred' ? 'warning.light' : 'error.light'
                          }}>
                            <PetsIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {patient.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {getSexLabel(patient.sex)} • {patient.weight || 'N/A'}kg
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {patient.species}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {patient.breed}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {patient.clientName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          N/A
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          N/A
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">
                            {patient.nextVaccinationDue || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(patient.status)}
                          color={getStatusColor(patient.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <ActionButtons
                          onView={() => handleViewAnimal(patient.id)}
                          onEdit={() => {}}
                          customActions={[
                            {
                              icon: <QrIcon />,
                              tooltip: 'QR Code',
                              onClick: () => handleGenerateQRCode(patient.id)
                            }
                          ]}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={allPatients.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>
      </Card>

      {/* Create Patient Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600 }}>
          Pacient Nou
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Pet Information */}
            <Grid size={12}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Informatii Animal
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Nume Animal"
                value={newPatient.name || ''}
                onChange={(e) => setNewPatient({...newPatient, name: e.target.value})}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Specie</InputLabel>
                <Select
                  value={newPatient.species || ''}
                  onChange={(e) => setNewPatient({...newPatient, species: e.target.value})}
                  label="Specie"
                >
                  <MenuItem value="dog">Caine</MenuItem>
                  <MenuItem value="cat">Pisica</MenuItem>
                  <MenuItem value="rabbit">Iepure</MenuItem>
                  <MenuItem value="other">Hamster</MenuItem>
                  <MenuItem value="bird">Pasare</MenuItem>
                  <MenuItem value="other">Alta specie</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Rasa"
                value={newPatient.breed || ''}
                onChange={(e) => setNewPatient({...newPatient, breed: e.target.value})}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Sex</InputLabel>
                <Select
                  value={newPatient.gender || ''}
                  onChange={(e) => setNewPatient({...newPatient, gender: e.target.value})}
                  label="Sex"
                >
                  <MenuItem value="mascul">Mascul</MenuItem>
                  <MenuItem value="feminin">Feminin</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Data Nasterii"
                type="date"
                value={newPatient.birthDate || ''}
                onChange={(e) => setNewPatient({...newPatient, birthDate: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Greutate (kg)"
                type="number"
                value={newPatient.weight || ''}
                onChange={(e) => setNewPatient({...newPatient, weight: e.target.value})}
                inputProps={{ min: 0, step: 0.1 }}
              />
            </Grid>

            {/* Owner Information */}
            <Grid size={12}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2, mt: 2 }}>
                Informatii Proprietar
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Nume Proprietar"
                value={newPatient.ownerName || ''}
                onChange={(e) => setNewPatient({...newPatient, ownerName: e.target.value})}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Telefon"
                value={newPatient.ownerPhone || ''}
                onChange={(e) => setNewPatient({...newPatient, ownerPhone: e.target.value})}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newPatient.ownerEmail || ''}
                onChange={(e) => setNewPatient({...newPatient, ownerEmail: e.target.value})}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Adresa"
                value={newPatient.ownerAddress || ''}
                onChange={(e) => setNewPatient({...newPatient, ownerAddress: e.target.value})}
              />
            </Grid>

            {/* Medical Information */}
            <Grid size={12}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2, mt: 2 }}>
                Informatii Medicale
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Cip ID *"
                required
                value={newPatient.chipId || ''}
                onChange={(e) => setNewPatient({...newPatient, chipId: e.target.value})}
                error={!newPatient.chipId}
                helperText={!newPatient.chipId ? "Cip ID este obligatoriu pentru identificarea unica a animalului" : ""}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Ultima Vaccinare"
                type="date"
                value={newPatient.lastVaccination || ''}
                onChange={(e) => setNewPatient({...newPatient, lastVaccination: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                label="Alergii cunoscute"
                multiline
                rows={2}
                value={newPatient.allergies || ''}
                onChange={(e) => setNewPatient({...newPatient, allergies: e.target.value})}
                placeholder="Lista alergiilor cunoscute..."
              />
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                label="Observatii medicale"
                multiline
                rows={3}
                value={newPatient.medicalNotes || ''}
                onChange={(e) => setNewPatient({...newPatient, medicalNotes: e.target.value})}
                placeholder="Observatii suplimentare despre starea de sanatate..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseCreateDialog} color="inherit">
            Anuleaza
          </Button>
          <Button 
            onClick={handleSavePatient} 
            variant="contained" 
            color="primary"
            disabled={!newPatient.name || !newPatient.species || !newPatient.gender || !newPatient.chipId || !newPatient.ownerName || !newPatient.ownerPhone}
          >
            Creeaza Pacientul
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />

      {/* Animal Card Dialog */}
      {selectedAnimalId && (
        <Dialog
          open={!!selectedAnimalId}
          onClose={handleCloseAnimalCard}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: { height: '90vh' }
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <AnimalCard 
              animalId={selectedAnimalId} 
              onClose={handleCloseAnimalCard}
            />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default PatientsManagement;

